import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Renders `children` as a floating popover anchored directly below `anchorRef.current`.
 * Used to drop the Friends and Messages panels right under their toolbar buttons
 * instead of letting them flow off-screen as side panels.
 *
 * Props:
 *   - open:           boolean — whether to render the popover
 *   - anchorRef:      React ref pointing at the trigger element
 *   - onOutsideClick: called when the user clicks outside the popover OR the trigger
 *   - children:       the panel to render
 *
 * Position recomputes on window resize / scroll. Clamped to the viewport so the popover
 * never falls off the right or bottom edge.
 */
const AnchoredPopover = ({ open, anchorRef, onOutsideClick, children }) => {
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

  // Compute position whenever the popover opens / window changes
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const anchor = anchorRef?.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const popWidth = popRef.current?.offsetWidth || 280;
      const popHeight = popRef.current?.offsetHeight || 320;
      let left = rect.left;
      let top = rect.bottom + 4;
      // Clamp horizontally — never let it bleed past the right edge
      if (left + popWidth > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - popWidth - 8);
      }
      if (left < 8) left = 8;
      // Clamp vertically — flip above the button if there's not enough room below
      if (top + popHeight > window.innerHeight - 8) {
        const flipped = rect.top - popHeight - 4;
        if (flipped >= 8) {
          top = flipped;
        } else {
          top = Math.max(8, window.innerHeight - popHeight - 8);
        }
      }
      setPos({ top, left, ready: true });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, anchorRef]);

  // Click-outside handler
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onOutsideClick?.();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, anchorRef, onOutsideClick]);

  if (!open) return null;

  return (
    <div
      ref={popRef}
      className="fixed z-[9000] shadow-2xl"
      style={{
        top: pos.top,
        left: pos.left,
        visibility: pos.ready ? "visible" : "hidden",
      }}
      data-testid="anchored-popover"
    >
      {children}
    </div>
  );
};

export default AnchoredPopover;
